import React, { useEffect, useMemo, useState } from 'react';
import ReactStars from 'react-rating-stars-component';
import styled from 'styled-components';

import FirebaseImage from '../Media/FirebaseImage';
import Loader from '../UI/Loader/Loader';
import TrustPassport from '../Trust/TrustPassport';
import ReviewList from '../Trust/ReviewList';
import { formatDate } from '../../Utils/date';
import {
  getPortfolioBeforeAfter,
  getPortfolioMedia,
  getPortfolioSummary,
  getPortfolioTitle,
  PORTFOLIO_MEDIA_SOURCE,
} from '../../Utils/portfolio';
import { regionLabel } from '../../Config/franceRegions';

const PublicProfilePage = ({
  profile,
  projects = [],
  role,
  loading,
  onBack,
  onProject,
}) => {
  const isArtisan = role === 'artisan';
  const fullName = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ') || (isArtisan ? 'Artisan' : 'Client');
  const rating = Number(profile?.average_rating || profile?.trust?.average_rating || profile?.artisan_rating || profile?.client_rating) || 0;
  const [activeCategory, setActiveCategory] = useState('all');
  const categories = useMemo(() => [...new Set(
    projects.map((project) => project?.portfolio?.category).filter(Boolean),
  )], [projects]);
  const visibleProjects = useMemo(() => (
    activeCategory === 'all'
      ? projects
      : projects.filter((project) => project?.portfolio?.category === activeCategory)
  ), [activeCategory, projects]);

  useEffect(() => {
    if (activeCategory !== 'all' && !categories.includes(activeCategory)) setActiveCategory('all');
  }, [activeCategory, categories]);

  if (loading && !profile) {
    return <StatePage><Loader /></StatePage>;
  }

  if (!profile) {
    return (
      <StatePage>
        <h1>Profil indisponible</h1>
        <p>Ce profil n’existe plus ou ne peut pas être consulté depuis ce lien.</p>
        <BackButton type="button" onClick={onBack}>Retour</BackButton>
      </StatePage>
    );
  }

  return (
    <Page>
      <Shell>
        <BackButton type="button" onClick={onBack}>← Retour</BackButton>

        <Hero>
          <AvatarWrap>
            <Avatar src={profile.profile_image} fallbackSrc="/images/user.svg" alt={`Portrait de ${fullName}`} />
          </AvatarWrap>
          <Identity>
            <RoleBadge>{isArtisan ? 'Artisan partenaire' : 'Client Le Petit Boulot'}</RoleBadge>
            <h1>{fullName}</h1>
            <ProfileLine>
              {isArtisan
                ? [profile.speciality, regionLabel(profile.artisan_region)].filter(Boolean).join(' · ') || 'Professionnel du bâtiment'
                : [profile.city, regionLabel(profile.region)].filter(Boolean).join(' · ') || 'Membre de la communauté'}
            </ProfileLine>
            {isArtisan && (profile.phone_number || profile.email) && (
              <ContactActions>
                {profile.phone_number && <ContactLink href={`tel:${profile.phone_number}`}>Appeler</ContactLink>}
                {profile.email && <ContactLink href={`mailto:${profile.email}`}>Envoyer un e-mail</ContactLink>}
              </ContactActions>
            )}
          </Identity>

          <Stats>
            <Stat>
              <span>{isArtisan ? 'Projets publiés' : 'Projets terminés'}</span>
              <strong>{isArtisan ? projects.length : projects.length}</strong>
            </Stat>
            <Stat>
              <span>Note moyenne</span>
              <strong>{rating ? `${rating.toFixed(1)}/5` : 'Nouveau'}</strong>
              <ReactStars count={5} value={rating} size={18} edit={false} isHalf activeColor="#c0a375" />
            </Stat>
          </Stats>
        </Hero>

        <TrustPassport profile={profile} role={isArtisan ? 'Artisan' : 'Client'} />

        <ReviewsCard>
          <ReviewList revieweeId={profile.user_id} limit={4} title={isArtisan ? 'Avis clients vérifiés' : 'Avis artisans vérifiés'} />
        </ReviewsCard>

        {isArtisan && (profile.business_intro || (Array.isArray(profile.certifications) && profile.certifications.length > 0)) && (
          <AboutSection>
            {profile.business_intro && (
              <div>
                <Eyebrow>À propos</Eyebrow>
                <AboutText>{profile.business_intro}</AboutText>
              </div>
            )}
            {Array.isArray(profile.certifications) && profile.certifications.length > 0 && (
              <div>
                <Eyebrow>Qualifications</Eyebrow>
                <CertificationList>
                  {profile.certifications.map((item) => <span key={item}>{item}</span>)}
                </CertificationList>
              </div>
            )}
          </AboutSection>
        )}

        <SectionHeader>
          <div>
            <Eyebrow>{isArtisan ? 'Portfolio' : 'Expériences vérifiées'}</Eyebrow>
            <h2>{isArtisan ? 'Réalisations évaluées' : 'Projets terminés avec des artisans'}</h2>
          </div>
          <Count>{projects.length} projet{projects.length > 1 ? 's' : ''}</Count>
        </SectionHeader>

        {isArtisan && categories.length > 1 && (
          <CategoryFilters aria-label="Filtrer les réalisations par catégorie">
            <CategoryButton type="button" $active={activeCategory === 'all'} onClick={() => setActiveCategory('all')}>
              Toutes
            </CategoryButton>
            {categories.map((category) => (
              <CategoryButton key={category} type="button" $active={activeCategory === category} onClick={() => setActiveCategory(category)}>
                {category}
              </CategoryButton>
            ))}
          </CategoryFilters>
        )}

        {projects.length === 0 ? (
          <Empty>
            <h3>Aucun projet public pour le moment</h3>
            <p>{isArtisan ? 'Cet artisan n’a pas encore sélectionné de réalisation à publier.' : 'Ce client n’a pas encore de projet terminé visible.'}</p>
          </Empty>
        ) : (
          <Gallery>
            {visibleProjects.map((project) => {
              const media = getPortfolioMedia(project);
              const image = media[0]?.url || '';
              const clientPhotoCount = media.filter((item) => item.source === PORTFOLIO_MEDIA_SOURCE.CLIENT).length;
              const artisanPhotoCount = media.filter((item) => item.source === PORTFOLIO_MEDIA_SOURCE.ARTISAN).length;
              const beforeAfter = getPortfolioBeforeAfter(project);
              const portfolio = project.portfolio || {};
              const projectTitle = getPortfolioTitle(project);
              const projectSummary = getPortfolioSummary(project);
              return (
                <ProjectButton key={project.demand_id} type="button" onClick={() => onProject(project)}>
                  <ProjectVisual>
                    <ProjectImage src={image} fallbackSrc="/images/project-placeholder.svg" alt={`Réalisation : ${projectTitle}`} />
                    {portfolio.featured && <FeaturedBadge>★ Projet à la une</FeaturedBadge>}
                    {beforeAfter && <BeforeAfterBadge>↔ Avant / Après</BeforeAfterBadge>}
                  </ProjectVisual>
                  <ProjectBody>
                    <ProjectTop>
                      <DateText>{formatDate(project.date)}</DateText>
                    </ProjectTop>
                    <h3>{projectTitle}</h3>
                    <p>{projectSummary || 'Consulter les photos et le détail du projet.'}</p>
                    <PublicMeta>
                      {portfolio.category && <span>{portfolio.category}</span>}
                      {portfolio.city && <span>{portfolio.city}</span>}
                      {portfolio.duration && <span>{portfolio.duration}</span>}
                    </PublicMeta>
                    <PhotoSources>
                      {clientPhotoCount > 0 && <span>Client · {clientPhotoCount}</span>}
                      {artisanPhotoCount > 0 && <span>Artisan · {artisanPhotoCount}</span>}
                    </PhotoSources>
                    <ProjectLink>Voir le projet →</ProjectLink>
                  </ProjectBody>
                </ProjectButton>
              );
            })}
          </Gallery>
        )}
      </Shell>
    </Page>
  );
};

const Page = styled.main`min-height: 100vh; padding: clamp(1.2rem, 4vw, 3rem) 1rem 4rem; background: #f4f7f9;`;
const Shell = styled.div`width: min(72rem, 100%); margin: 0 auto;`;
const BackButton = styled.button`
  margin-bottom: 1rem; padding: 0.6rem 0.9rem; border: 1px solid #e7dfd2; border-radius: 999px; background: #fff; color: #111111; font: inherit; font-weight: 800; cursor: pointer;
`;
const Hero = styled.section`
  display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: clamp(1rem, 3vw, 2rem); padding: clamp(1.3rem, 4vw, 2.4rem); border: 1px solid rgba(0, 0, 0, 0.08); border-radius: 1.5rem; background: linear-gradient(135deg, #fff 0%, #f4f8fb 100%); box-shadow: 0 1.2rem 3rem rgba(0, 0, 0, 0.08);
  @media (max-width: 760px) { grid-template-columns: auto 1fr; ${'' /* Stats spans below */} }
  @media (max-width: 520px) { grid-template-columns: 1fr; text-align: center; justify-items: center; }
`;
const AvatarWrap = styled.div`padding: 0.35rem; border: 3px solid #e4b34f; border-radius: 50%; background: #fff;`;
const Avatar = styled(FirebaseImage)`width: clamp(6rem, 14vw, 8.5rem); height: clamp(6rem, 14vw, 8.5rem); border-radius: 50%; object-fit: cover; background: #efede8;`;
const Identity = styled.div`
  min-width: 0;
  h1 { margin: 0.45rem 0 0.25rem; color: #111111; font-size: clamp(1.7rem, 4vw, 2.6rem); line-height: 1.15; }
  @media (max-width: 520px) { display: grid; justify-items: center; }
`;
const RoleBadge = styled.span`display: inline-flex; padding: 0.35rem 0.65rem; border-radius: 999px; background: #fff4d9; color: #795718; font-size: 0.72rem; font-weight: 900; letter-spacing: 0.08em; text-transform: uppercase;`;
const ProfileLine = styled.p`margin: 0; color: #63727d;`;
const ContactActions = styled.div`display: flex; flex-wrap: wrap; gap: 0.55rem; margin-top: 1rem; @media (max-width: 520px) { justify-content: center; }`;
const ContactLink = styled.a`padding: 0.58rem 0.82rem; border: 1px solid #e7dfd2; border-radius: 999px; background: #fff; color: #111111; font-weight: 800; font-size: 0.82rem; text-decoration: none; &:hover { border-color: #c0a375; }`;
const Stats = styled.div`
  display: grid; grid-template-columns: repeat(2, minmax(8rem, 1fr)); gap: 0.7rem;
  @media (max-width: 760px) { grid-column: 1 / -1; width: 100%; }
  @media (max-width: 520px) { grid-template-columns: 1fr 1fr; }
`;
const Stat = styled.div`
  display: grid; justify-items: center; gap: 0.2rem; padding: 0.9rem; border: 1px solid #e2e9ed; border-radius: 1rem; background: rgba(255,255,255,0.85); text-align: center;
  span { color: #6a7882; font-size: 0.72rem; text-transform: uppercase; }
  strong { color: #18344b; font-size: 1.15rem; }
`;
const ReviewsCard = styled.section`margin-top:1rem;padding:1rem;border:1px solid #e5e1da;border-radius:1.2rem;background:#fff;`;
const AboutSection = styled.section`
  display: grid; grid-template-columns: minmax(0,1.5fr) minmax(14rem,1fr); gap: 1rem; margin-top: 1rem; padding: 1.2rem; border: 1px solid #e5e1da; border-radius: 1.2rem; background: #fff;
  @media (max-width: 680px) { grid-template-columns: 1fr; }
`;
const AboutText = styled.p`margin: .45rem 0 0; color: #4f6473; line-height: 1.7; white-space: pre-wrap;`;
const CertificationList = styled.div`display: flex; flex-wrap: wrap; gap: .45rem; margin-top: .5rem; span { padding: .42rem .62rem; border-radius: 999px; background: #fff4d9; color: #745318; font-size: .75rem; font-weight: 850; }`;
const SectionHeader = styled.header`
  display: flex; align-items: flex-end; justify-content: space-between; gap: 1rem; margin: 2rem 0 1rem;
  h2 { margin: 0.2rem 0 0; color: #111111; font-size: clamp(1.35rem, 3vw, 1.9rem); }
`;
const Eyebrow = styled.div`color: #a88a5d; font-size: 0.74rem; font-weight: 900; letter-spacing: 0.12em; text-transform: uppercase;`;
const Count = styled.span`color: #677681; font-size: 0.85rem;`;
const CategoryFilters = styled.div`display: flex; gap: .5rem; overflow-x: auto; margin: -.25rem 0 1rem; padding-bottom: .25rem;`;
const CategoryButton = styled.button`
  min-width: max-content; padding: .55rem .75rem; border: 1px solid ${({ $active }) => ($active ? '#111111' : '#e7dfd2')}; border-radius: 999px; background: ${({ $active }) => ($active ? '#111111' : '#fff')}; color: ${({ $active }) => ($active ? '#fff' : '#5f5a52')}; font: inherit; font-size: .78rem; font-weight: 850; cursor: pointer;
`;
const Gallery = styled.div`display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 1rem; @media (max-width: 850px) { grid-template-columns: repeat(2, minmax(0, 1fr)); } @media (max-width: 560px) { grid-template-columns: 1fr; }`;
const ProjectButton = styled.button`
  min-width: 0; padding: 0; overflow: hidden; border: 1px solid #e5e1da; border-radius: 1.2rem; background: #fff; color: #111111; font: inherit; text-align: left; cursor: pointer; box-shadow: 0 0.8rem 2rem rgba(0,0,0,0.06); transition: transform 160ms ease, box-shadow 160ms ease;
  &:hover { transform: translateY(-3px); box-shadow: 0 1.2rem 2.5rem rgba(0,0,0,0.12); }
`;
const ProjectVisual = styled.div`position: relative; background: #eef2f4;`;
const ProjectImage = styled(FirebaseImage)`width: 100%; height: 13rem; object-fit: contain; padding: .5rem; box-sizing: border-box; background: #eef2f4;`;
const FeaturedBadge = styled.span`position: absolute; top: .7rem; left: .7rem; padding: .38rem .58rem; border-radius: 999px; background: rgba(255,244,217,.95); color: #715016; font-size: .7rem; font-weight: 900;`;
const BeforeAfterBadge = styled.span`position: absolute; right: .7rem; bottom: .7rem; padding: .38rem .58rem; border-radius: 999px; background: rgba(0,0,0,.9); color: #fff; font-size: .68rem; font-weight: 900; backdrop-filter: blur(8px);`;
const ProjectBody = styled.div`
  padding: 1rem;
  h3 { margin: 0.65rem 0 0.35rem; overflow: hidden; color: #111111; font-size: 1rem; line-height: 1.35; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
  p { margin: 0; overflow: hidden; color: #66615b; font-size: 0.84rem; line-height: 1.5; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
`;
const ProjectTop = styled.div`display: flex; align-items: center; justify-content: space-between; gap: 0.6rem;`;
const DateText = styled.span`color: #74818a; font-size: 0.76rem;`;
const PublicMeta = styled.div`display: flex; flex-wrap: wrap; gap: .35rem; margin-top: .7rem; span { padding: .25rem .45rem; border-radius: 999px; background: #f0f4f6; color: #5f707b; font-size: .68rem; font-weight: 750; }`;
const PhotoSources = styled.div`display: flex; flex-wrap: wrap; gap: .35rem; margin-top: .55rem; span { padding: .28rem .45rem; border-radius: 999px; background: #fff7e8; color: #785817; font-size: .66rem; font-weight: 850; }`;
const ProjectLink = styled.span`display: block; margin-top: 0.8rem; color: #8a641a; font-size: 0.82rem; font-weight: 900;`;
const Empty = styled.div`padding: 2rem; border: 1px dashed #ddd3c4; border-radius: 1rem; background: #fff; color: #65747f; text-align: center; h3 { margin-bottom: 0.35rem; color: #233b4e; } p { margin: 0; }`;
const StatePage = styled.main`display: grid; place-content: center; min-height: 70vh; padding: 2rem; background: #f4f7f9; color: #243d50; text-align: center; p { color: #66615b; }`;

export default PublicProfilePage;
